import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 *
 * @returns {((prompt: string) => void) | null}
 */
function getEnviveOpener() {
  const envive = /** @type {any} */ (window).spiffy ?? /** @type {any} */ (window)._spiffy;
  const openChatWithPrompt = envive?.openChatWithPrompt;
  return typeof openChatWithPrompt === 'function' ? openChatWithPrompt : null;
}

/**
 * @typedef {object} KcsRefs
 * @property {HTMLDialogElement} dialog
 *
 * @extends Component<KcsRefs>
 */
class KcsSearchComponent extends Component {
  requiredRefs = ['dialog'];

  #previousScrollY = 0;

  showDialog = () => {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new CustomEvent('dialog:open'));

      /** @type {HTMLElement | null} */ (dialog.querySelector('.kcs-bar__input'))?.focus();

      dialog.addEventListener('cancel', this.#handleCancel);
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  };

  closeDialog = () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.removeEventListener('cancel', this.#handleCancel);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    this.dispatchEvent(new CustomEvent('dialog:close'));
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };
}

if (!customElements.get('kcs-search-component')) {
  customElements.define('kcs-search-component', KcsSearchComponent);
}

/**
 * Layout/styling host for the KC search field — the Envive submit behaviour lives in
 * `envive-prompt-field`, which `_kcs-search-bar` wraps around the form.
 *
 * @extends {Component}
 */
class KcsSearchField extends Component {}

if (!customElements.get('kcs-search-field')) {
  customElements.define('kcs-search-field', KcsSearchField);
}

class KcsRotatingText extends HTMLElement {
  /** @type {string[]} */
  #questions = [];
  #idx = 0;
  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;
  /** @type {HTMLElement | null} */
  #host = null;

  connectedCallback() {
    this.#host = this.closest('kcs-search-component');
    this.#questions = [...(this.#host?.querySelectorAll('template[data-question]') ?? [])]
      .map((t) => /** @type {HTMLElement} */ (t).dataset.question?.trim() ?? '')
      .filter((q) => q !== '');
    if (this.#questions.length > 0) this.textContent = this.#questions[0] ?? null;
    if (this.#questions.length < 2) return;

    const ms = Number(this.dataset.interval) || 4000;
    this.#start(ms);

    this.#host?.addEventListener('dialog:open', this.#pause);
    this.#host?.addEventListener('dialog:close', this.#resume);
  }

  disconnectedCallback() {
    clearInterval(this.#timer ?? undefined);
    this.#host?.removeEventListener('dialog:open', this.#pause);
    this.#host?.removeEventListener('dialog:close', this.#resume);
    this.#host = null;
  }

  /** @param {number} ms */
  #start(ms) {
    this.#timer = setInterval(() => this.#next(), ms);
  }

  #next = () => {
    this.style.opacity = '0';
    setTimeout(() => {
      this.#idx = (this.#idx + 1) % this.#questions.length;
      this.textContent = this.#questions[this.#idx] ?? null;
      this.style.opacity = '1';
    }, 200);
  };

  #pause = () => {
    clearInterval(this.#timer ?? undefined);
    this.#timer = null;
  };

  #resume = () => {
    if (this.#timer) return;
    this.#start(Number(this.dataset.interval) || 4000);
  };
}

if (!customElements.get('kcs-rotating-text')) {
  customElements.define('kcs-rotating-text', KcsRotatingText);
}

/**
 * Sends the typed question straight to the Envive chat — the form never submits.
 * Direct listeners on the element, not Horizon's `on:submit` delegation, which does not
 * reach these blocks.
 */
class EnvivePromptField extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#openChat);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#openChat);
    this.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Enter in the input does not reach the form's `submit` event here, so open the chat
   * from the keypress itself.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    this.#openChat(event);
  };

  /** @param {Event} event */
  #openChat = (event) => {
    event.preventDefault();

    const input = this.querySelector('[data-envive-input], .kcs-bar__input');
    const query = input instanceof HTMLInputElement ? input.value.trim() : '';
    if (!query) return;

    const openChatWithPrompt = getEnviveOpener();
    if (!openChatWithPrompt) return;

    const template = this.querySelector('form')?.dataset.promptTemplate || '[query]';
    const prompt = template.includes('[query]')
      ? template.split('[query]').join(query)
      : `${template} ${query}`.trim();

    // Inside the search drawer, get the dialog out of the way of the chat.
    const widget = /** @type {any} */ (this.closest('kcs-search-component'));
    if (widget && typeof widget.closeDialog === 'function') widget.closeDialog();

    openChatWithPrompt(prompt);
  };
}

/** The Envive Center block's own tag, so its markup stays unwrapped. */
class KcEnviveCenter extends EnvivePromptField {}

if (!customElements.get('envive-prompt-field')) {
  customElements.define('envive-prompt-field', EnvivePromptField);
}

if (!customElements.get('kc-envive-center')) {
  customElements.define('kc-envive-center', KcEnviveCenter);
}

if (!(/** @type {any} */ (window).__envivePromptDelegate)) {
  /** @type {any} */ (window).__envivePromptDelegate = true;
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const el = target.closest('[data-envive-prompt]');
    if (!(el instanceof HTMLElement)) return;
    const prompt = el.dataset.envivePrompt?.trim();
    if (!prompt) return;
    const openChatWithPrompt = getEnviveOpener();
    if (!openChatWithPrompt) return; // fall through to the link's href
    event.preventDefault();

    // Pills in the search drawer: get the dialog out of the way of the chat.
    const widget = /** @type {any} */ (el.closest('kcs-search-component'));
    if (widget && typeof widget.closeDialog === 'function') widget.closeDialog();

    openChatWithPrompt(prompt);
  });
}